import { useState } from "react";
import { Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { updatePayment } from "../lib/api";
import { PaymentPart } from "./PaymentPart";
import { money } from "../lib/payments";

const Field = ({ label, testid, ...props }) => (
  <label className="block min-w-0">
    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
    <input data-testid={testid} {...props}
      className="mt-1 w-full bg-[#0B0C0E] border border-[#262C38] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F95722] [color-scheme:dark]" />
  </label>
);

const FIELDS = ["carrier_name", "carrier_email", "start_date", "carrier_total", "setter_amount"];

export const PaymentCard = ({ payment, onChange }) => {
  const init = () => Object.fromEntries(FIELDS.map((f) => [f, payment[f] ?? ""]));
  const [form, setForm] = useState(init);
  const [busy, setBusy] = useState(false);
  const dirty = FIELDS.some((f) => String(form[f]) !== String(payment[f] ?? ""));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, carrier_total: form.carrier_total === "" ? null : Number(form.carrier_total),
        setter_amount: form.setter_amount === "" ? null : Number(form.setter_amount), start_date: form.start_date || null };
      const { data } = await updatePayment(payment.id, body);
      onChange(data);
      toast.success("Payment details saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const id = payment.id;
  return (
    <div data-testid={`payment-card-${id}`} className="bg-[#13161C] border border-[#262C38] rounded-md p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <div>
          <h3 data-testid={`payment-driver-${id}`} className="font-heading text-lg font-bold text-white">{payment.driver_name}</h3>
          <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Set by {payment.setter_name || "—"}</p>
        </div>
        <span className="font-mono text-xs text-slate-400 inline-flex items-center gap-1"><Truck className="h-3.5 w-3.5" />{payment.carrier_name || "No carrier yet"}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Field label="Carrier name" testid={`payment-carrier-name-input-${id}`} value={form.carrier_name} onChange={set("carrier_name")} placeholder="e.g. Swift Transport" />
        <Field label="Carrier billing email" testid={`payment-carrier-email-input-${id}`} type="email" value={form.carrier_email} onChange={set("carrier_email")} placeholder="billing@carrier.com" />
        <Field label="Driver start date" testid={`payment-start-date-input-${id}`} type="date" value={form.start_date} onChange={set("start_date")} />
        <Field label="Carrier pays total ($)" testid={`payment-carrier-total-input-${id}`} type="number" min="0" value={form.carrier_total} onChange={set("carrier_total")} placeholder="3000" />
        <Field label="Setter payout total ($)" testid={`payment-setter-amount-input-${id}`} type="number" min="0" value={form.setter_amount} onChange={set("setter_amount")} placeholder="500" />
      </div>
      <div className="flex justify-end mt-3">
        <button data-testid={`payment-save-btn-${id}`} onClick={save} disabled={busy || !dirty}
          className="inline-flex items-center gap-1.5 bg-[#F95722] hover:bg-[#ff6a39] text-white font-bold px-4 py-2 rounded text-xs uppercase tracking-wide disabled:opacity-40">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save details
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        <div className="bg-[#0B0C0E] border border-[#262C38] rounded px-3 py-1">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 pt-2">From carrier · {money(payment.carrier_total)}</p>
          <PaymentPart payment={payment} part="carrier_1" label="1st half — start day" onChange={onChange} dirty={dirty} />
          <PaymentPart payment={payment} part="carrier_2" label="2nd half — 2 weeks later" onChange={onChange} dirty={dirty} />
        </div>
        <div className="bg-[#0B0C0E] border border-[#262C38] rounded px-3 py-1">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 pt-2">To setter · {money(payment.setter_amount)}</p>
          <PaymentPart payment={payment} part="setter_1" label="1st half — start day" onChange={onChange} />
          <PaymentPart payment={payment} part="setter_2" label="2nd half — 2 weeks later" onChange={onChange} />
        </div>
      </div>
    </div>
  );
};
